<template>
  <div>
    <div class="flex items-end justify-start gap-2 w-full">
      <TextInput
        id="gift-card-number-input"
        class="flex-grow"
        forInput="gift-card-number-input"
        v-model.trim="giftCardNumber"
        label="Gift Card Number"
        placeholder="Gift Card Number"
      />
      <TextInput
        class="w-1/5"
        id="gift-card-pin-input"
        forInput="gift-card-pin-input"
        v-model.trim="giftCardPin"
        label="Gift Card Pin"
        placeholder="Gift Card Pin"
      />
      <ButtonDefault
        @click="onButtonClick"
        id="gift-card-submit-button"
        class="px-4 h-12 uppercase border border-solid rounded font-semibold bg-button-guest-primary font-button text-button-label border-button-guest-primary disabled:bg-disabled disabled:cursor-not-allowed disabled:border-disabled disabled:text-white"
        :label="buttonLabel"
        :disabled="isButtonDisabled"
        guest
      />
    </div>
    <p
      v-if="error.message"
      id="gift-card-error"
      class="p-2 mt-4 rounded border border-red-400 bg-red-200 text-red-600"
    >
      {{ error.message }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import TextInput from '@/common/components/molecules/TextInput.vue'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { ErrorData, blankError, displayHandled } from '@/utils/errors'

const props = defineProps<{
  acceptsCheckoutConfirmation?: boolean
}>()

const emit = defineEmits<{
  // eslint-disable-next-line no-unused-vars
  (e: 'partialPayment'): void
}>()

const checkoutStore = useCheckoutStore()
const settingStore = useSettingStore()
const { reservation } = storeToRefs(checkoutStore)
const { checkout_confirmation_text } = storeToRefs(settingStore)

const giftCardNumber = ref<string>('')
const giftCardPin = ref<string>('')
const isPayingWithGiftCard = ref<boolean>(false)
const error = ref<ErrorData>(blankError())
const invalidToSubmit = computed<boolean>(
  () => !giftCardNumber.value || !giftCardPin.value || !reservation.value,
)

const buttonLabel = computed<string>(() => {
  return isPayingWithGiftCard.value ? 'Submitting...' : 'Submit'
})

const isButtonDisabled = computed<boolean>(() => {
  return (
    isPayingWithGiftCard.value ||
    invalidToSubmit.value ||
    (!!checkout_confirmation_text.value && !props.acceptsCheckoutConfirmation)
  )
})

async function onButtonClick() {
  if (isButtonDisabled.value) return

  isPayingWithGiftCard.value = true
  try {
    const paid = await checkoutStore.checkoutWithGiftCard(giftCardNumber.value, giftCardPin.value)
    if (paid === false) emit('partialPayment')
  } catch (e) {
    displayHandled(e, error.value)
  } finally {
    isPayingWithGiftCard.value = false
  }
}
</script>
